// generic confirmation modal for delete/duplicate actions
// returns a promise that resolves with the user's choice

let activeModal = null;

export function showConfirmModal(options = {}) {
  const {
    title = "Confirm",
    message = "Are you sure?",
    confirmText = "Confirm",
    cancelText = "Cancel",
    confirmClass = "",
    // for multi-choice modals (like duplicate detection)
    choices = null,
  } = options;

  // close any existing modal
  if (activeModal) {
    activeModal.remove();
    activeModal = null;
  }

  return new Promise((resolve) => {
    const backdrop = document.createElement("div");
    backdrop.className = "confirm-modal-backdrop";

    const modal = document.createElement("div");
    modal.className = "confirm-modal";
    modal.setAttribute("role", "dialog");
    modal.setAttribute("aria-modal", "true");
    modal.setAttribute("aria-labelledby", "confirm-modal-title");

    const titleEl = document.createElement("h3");
    titleEl.id = "confirm-modal-title";
    titleEl.className = "confirm-modal__title";
    titleEl.textContent = title;

    const messageEl = document.createElement("p");
    messageEl.className = "confirm-modal__message";
    messageEl.textContent = message;

    const actionsEl = document.createElement("div");
    actionsEl.className = "confirm-modal__actions";

    function cleanup(result) {
      backdrop.remove();
      activeModal = null;
      document.removeEventListener("keydown", handleKeydown);
      resolve(result);
    }

    function handleKeydown(event) {
      if (event.key === "Escape") {
        cleanup(choices ? "cancel" : false);
      }
    }

    if (choices && Array.isArray(choices)) {
      // multi-choice modal (for duplicate detection)
      choices.forEach((choice) => {
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = `confirm-modal__btn ${choice.class || ""}`;
        btn.textContent = choice.text;
        btn.addEventListener("click", () => cleanup(choice.value));
        actionsEl.appendChild(btn);
      });
    } else {
      // simple confirm/cancel modal
      const cancelBtn = document.createElement("button");
      cancelBtn.type = "button";
      cancelBtn.className = "confirm-modal__btn confirm-modal__btn--cancel";
      cancelBtn.textContent = cancelText;
      cancelBtn.addEventListener("click", () => cleanup(false));

      const confirmBtn = document.createElement("button");
      confirmBtn.type = "button";
      confirmBtn.className = `confirm-modal__btn confirm-modal__btn--confirm ${confirmClass}`;
      confirmBtn.textContent = confirmText;
      confirmBtn.addEventListener("click", () => cleanup(true));

      actionsEl.appendChild(cancelBtn);
      actionsEl.appendChild(confirmBtn);
    }

    modal.appendChild(titleEl);
    modal.appendChild(messageEl);
    modal.appendChild(actionsEl);
    backdrop.appendChild(modal);

    document.body.appendChild(backdrop);
    activeModal = backdrop;

    // focus first button for keyboard accessibility
    const firstBtn = actionsEl.querySelector("button");
    if (firstBtn) {
      firstBtn.focus();
    }

    // close on backdrop click
    backdrop.addEventListener("click", (event) => {
      if (event.target === backdrop) {
        cleanup(choices ? "cancel" : false);
      }
    });

    document.addEventListener("keydown", handleKeydown);
  });
}

// convenience function for delete confirmation
export function showDeleteConfirm(itemName) {
  return showConfirmModal({
    title: "Delete transcript?",
    message: `This will permanently delete "${itemName}" and cannot be undone.`,
    confirmText: "Delete",
    cancelText: "Keep",
    confirmClass: "confirm-modal__btn--danger",
  });
}

// convenience function for duplicate detection
export function showDuplicatePrompt(existingName, confidence) {
  const confidenceText =
    confidence === "exact"
      ? "This file appears to be identical to"
      : confidence === "high"
        ? "This file has the same name and size as"
        : "This file has the same name as";

  return showConfirmModal({
    title: "Duplicate detected",
    message: `${confidenceText} "${existingName}" in your library.`,
    choices: [
      {
        text: "Replace existing",
        value: "replace",
        class: "confirm-modal__btn--danger",
      },
      {
        text: "Upload as new",
        value: "new",
        class: "confirm-modal__btn--primary",
      },
      { text: "Cancel", value: "cancel", class: "confirm-modal__btn--cancel" },
    ],
  });
}
